import { useCallback, useEffect, useState } from 'react';
import { addMonths, startOfMonth } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabaseClient';
import { GBP_ENABLED } from '../lib/featureFlags';

export type GbpControlCentreRating = {
  /** True when at least one active Zernio GBP connection exists. */
  connected: boolean;
  /** Average star rating across connected locations (weighted by review count when available). */
  rating: number | null;
  /** Reviews received so far this month, summed across connected locations. Null until loaded. */
  reviewCount: number | null;
  /** Reviews in the same elapsed stretch of last month, for the month-over-month delta. */
  previousReviewCount: number | null;
  locationCount: number;
  /** True while checking whether GBP is connected (badge hidden). */
  loading: boolean;
  /** True while fetching the live star rating (badge visible with empty stars). */
  ratingLoading: boolean;
};

/** This month so far, and the same amount of time last month, in the business timezone. */
function reviewMonthWindows(timezone: string, now = new Date()) {
  const zonedNow = toZonedTime(now, timezone);
  const monthStart = fromZonedTime(startOfMonth(zonedNow), timezone);
  const nextMonthStart = fromZonedTime(startOfMonth(addMonths(zonedNow, 1)), timezone);
  const lastMonthStart = fromZonedTime(startOfMonth(addMonths(zonedNow, -1)), timezone);
  const elapsed = now.getTime() - monthStart.getTime();
  const previousEnd = new Date(Math.min(lastMonthStart.getTime() + elapsed, monthStart.getTime()));
  return {
    monthStartMs: monthStart.getTime(),
    monthEndMs: nextMonthStart.getTime(),
    previousStartMs: lastMonthStart.getTime(),
    previousEndMs: previousEnd.getTime(),
  };
}

function countCreatedBetween(
  reviews: Array<{ createTime?: string | null }>,
  startMs: number,
  endMs: number,
) {
  let count = 0;
  for (const review of reviews) {
    const created = Date.parse(String(review?.createTime || ''));
    if (Number.isFinite(created) && created >= startMs && created < endMs) count += 1;
  }
  return count;
}

/**
 * Control Centre banner: Google Business star rating for the selected business.
 * Connection state resolves first so the badge can render immediately; rating fills in after.
 * The review count is reviews received this month, not Google's lifetime total.
 */
export function useGbpControlCentreRating(): GbpControlCentreRating {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timezone = selectedBusiness?.timezone?.trim() || 'America/Vancouver';
  const [connected, setConnected] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [reviewCount, setReviewCount] = useState<number | null>(null);
  const [previousReviewCount, setPreviousReviewCount] = useState<number | null>(null);
  const [locationCount, setLocationCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [ratingLoading, setRatingLoading] = useState(false);

  const load = useCallback(async () => {
    if (!GBP_ENABLED || !businessId) {
      setConnected(false);
      setRating(null);
      setReviewCount(null);
      setPreviousReviewCount(null);
      setLocationCount(0);
      setLoading(false);
      setRatingLoading(false);
      return;
    }

    setLoading(true);
    setConnected(false);
    setRating(null);
    setReviewCount(null);
    setPreviousReviewCount(null);
    setLocationCount(0);
    setRatingLoading(false);

    try {
      const { data: conns } = await supabase
        .from('google_business_connections')
        .select('id')
        .eq('business_id', businessId)
        .in('provider', ['ayrshare', 'google'])
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      const rows = conns || [];
      if (rows.length === 0) {
        setConnected(false);
        return;
      }

      // Show the badge as soon as we know GBP is connected — rating fills in next.
      setConnected(true);
      setLocationCount(rows.length);
      setLoading(false);
      setRatingLoading(true);

      const results = await Promise.all(
        rows.map(async (row) => {
          const { data, error } = await supabase.functions.invoke('ayrshare-gbp-reviews', {
            body: {
              businessId,
              connectionId: row.id,
              pageSize: 1,
            },
          });
          if (error || data?.error) return null;
          const avg =
            typeof data?.averageRating === 'number' && Number.isFinite(data.averageRating)
              ? data.averageRating
              : null;
          const count =
            typeof data?.totalReviewCount === 'number' && Number.isFinite(data.totalReviewCount)
              ? Math.max(0, data.totalReviewCount)
              : 0;
          const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
          return { avg, count, reviews };
        }),
      );

      const valid = results.filter(
        (r): r is { avg: number | null; count: number; reviews: Array<{ createTime?: string | null }> } =>
          r != null,
      );
      if (valid.length === 0) {
        setRating(null);
        setReviewCount(null);
        setPreviousReviewCount(null);
        return;
      }

      const windows = reviewMonthWindows(timezone);
      const thisMonth = valid.reduce(
        (sum, r) => sum + countCreatedBetween(r.reviews, windows.monthStartMs, windows.monthEndMs),
        0,
      );
      const lastMonth = valid.reduce(
        (sum, r) => sum + countCreatedBetween(r.reviews, windows.previousStartMs, windows.previousEndMs),
        0,
      );
      const rated = valid.filter((r): r is { avg: number; count: number; reviews: Array<{ createTime?: string | null }> } => r.avg != null);
      const ratedCount = rated.reduce((sum, r) => sum + r.count, 0);
      const blended = rated.length === 0
        ? null
        : ratedCount > 0
          ? rated.reduce((sum, r) => sum + r.avg * r.count, 0) / ratedCount
          : rated.reduce((sum, r) => sum + r.avg, 0) / rated.length;

      setRating(blended == null ? null : Math.round(blended * 10) / 10);
      setReviewCount(thisMonth);
      setPreviousReviewCount(lastMonth);
    } catch (err) {
      console.warn('[useGbpControlCentreRating]', err);
      setConnected(false);
      setRating(null);
      setReviewCount(null);
      setPreviousReviewCount(null);
      setLocationCount(0);
    } finally {
      setLoading(false);
      setRatingLoading(false);
    }
  }, [businessId, timezone]);

  useEffect(() => {
    void load();
  }, [load]);

  return { connected, rating, reviewCount, previousReviewCount, locationCount, loading, ratingLoading };
}
